import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from '@mui/material';
import { Button } from '@/shared/ui';
import {
  baseCoverScale,
  clampCropPan,
  cropAreaFromPan,
  cropImageToBlob,
} from './cropImageToBlob';

const VIEWPORT_SIZE = 280;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const OUTPUT_SIZE = 512;

export interface AvatarCropDialogLabels {
  title: string;
  hint: string;
  zoom: string;
  cancel: string;
  confirm: string;
}

export interface AvatarCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  fileName?: string;
  labels: AvatarCropDialogLabels;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (file: File) => Promise<void>;
}

export function AvatarCropDialog({
  open,
  imageSrc,
  fileName = 'avatar.jpg',
  labels,
  busy,
  onClose,
  onConfirm,
}: AvatarCropDialogProps) {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(
    null
  );

  const baseScale = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height) return 1;
    return baseCoverScale(VIEWPORT_SIZE, naturalSize.width, naturalSize.height);
  }, [naturalSize.height, naturalSize.width]);

  const displayScale = baseScale * zoom;
  const displayWidth = naturalSize.width * displayScale;
  const displayHeight = naturalSize.height * displayScale;

  const centerPan = useCallback(() => {
    setPan(
      clampCropPan({
        x: (VIEWPORT_SIZE - naturalSize.width * baseScale * zoom) / 2,
        y: (VIEWPORT_SIZE - naturalSize.height * baseScale * zoom) / 2,
        viewportSize: VIEWPORT_SIZE,
        displayWidth: naturalSize.width * baseScale * zoom,
        displayHeight: naturalSize.height * baseScale * zoom,
      })
    );
  }, [baseScale, naturalSize.height, naturalSize.width, zoom]);

  useEffect(() => {
    if (!open || !imageSrc) {
      setNaturalSize({ width: 0, height: 0 });
      setZoom(MIN_ZOOM);
      setPan({ x: 0, y: 0 });
      return;
    }
    const image = new Image();
    image.onload = () => {
      setNaturalSize({ width: image.naturalWidth, height: image.naturalHeight });
      setZoom(MIN_ZOOM);
    };
    image.src = imageSrc;
  }, [imageSrc, open]);

  useEffect(() => {
    if (!open || !naturalSize.width) return;
    centerPan();
  }, [centerPan, naturalSize.width, open, zoom]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (busy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: pan.x,
      originY: pan.y,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || busy) return;
    const next = clampCropPan({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
      viewportSize: VIEWPORT_SIZE,
      displayWidth,
      displayHeight,
    });
    setPan(next);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleConfirm = async () => {
    if (!imageSrc || !naturalSize.width || busy) return;
    const crop = cropAreaFromPan({
      panX: pan.x,
      panY: pan.y,
      viewportSize: VIEWPORT_SIZE,
      scale: displayScale,
    });
    const blob = await cropImageToBlob(imageSrc, crop, OUTPUT_SIZE);
    const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : '.jpg';
    const safeName = fileName.replace(/\.[^.]+$/, '') || 'avatar';
    const file = new File([blob], `${safeName}${ext === '.png' || ext === '.webp' ? ext : '.jpg'}`, {
      type: blob.type || 'image/jpeg',
    });
    await onConfirm(file);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{labels.title}</DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {labels.hint}
        </Typography>
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            position: 'relative',
            width: VIEWPORT_SIZE,
            height: VIEWPORT_SIZE,
            mx: 'auto',
            overflow: 'hidden',
            borderRadius: '50%',
            bgcolor: 'action.hover',
            touchAction: 'none',
            cursor: busy ? 'default' : 'grab',
          }}
        >
          {imageSrc && naturalSize.width ? (
            <Box
              component="img"
              src={imageSrc}
              alt=""
              draggable={false}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: displayWidth,
                height: displayHeight,
                transform: `translate(${pan.x}px, ${pan.y}px)`,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          ) : null}
        </Box>
        <Box sx={{ mt: 2, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {labels.zoom}
          </Typography>
          <Slider
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            disabled={!naturalSize.width || busy}
            onChange={(_, value) => setZoom(Array.isArray(value) ? value[0]! : value)}
            aria-label={labels.zoom}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose} disabled={busy}>
          {labels.cancel}
        </Button>
        <Button variant="contained" onClick={() => void handleConfirm()} disabled={!imageSrc || busy}>
          {labels.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
